import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as authApi from '../api/endpoints/auth';
import { AUTH_SESSION_STORAGE_KEY, getCurrentSessionId, getStoredTokens, storeTokens, clearTokens, setOnSessionExpired } from '../api/client';
import { TOKEN_STORAGE_KEYS } from '../config/api';
import { clearRecentSearches } from '../utils/recentSearches';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [customer, setCustomerState] = useState(null);
  const [authStatus, setAuthStatus] = useState('loading');
  const [validatedSession, setValidatedSession] = useState(null);
  const sessionRevisionRef = useRef(0);
  const candidateSessionRef = useRef(null);

  useEffect(() => {
    setOnSessionExpired(() => {
      sessionRevisionRef.current += 1;
      clearTokens();
      clearRecentSearches();
      candidateSessionRef.current = null;
      setValidatedSession(null);
      setCustomerState(null);
      setAuthStatus('guest');
    });
    return () => setOnSessionExpired(null);
  }, []);

  // A token in storage is only a session candidate. Keep protected routes in
  // their loading state until /me validates it (including one refresh retry),
  // otherwise a stale token can mount authenticated pages indefinitely.
  const validateSession = useCallback(() => {
    const { accessToken } = getStoredTokens();
    const session = getCurrentSessionId();
    const revision = ++sessionRevisionRef.current;
    candidateSessionRef.current = session;
    setValidatedSession(null);
    setCustomerState(null);
    if (!accessToken) {
      setAuthStatus('guest');
      return;
    }
    setAuthStatus('loading');
    const isCurrent = () => revision === sessionRevisionRef.current && session === getCurrentSessionId();
    Promise.resolve()
      .then(() => authApi.getMe({ session }))
      .then((response) => {
        if (!isCurrent()) return;
        setCustomerState(response.data);
        setValidatedSession(session);
        setAuthStatus('authenticated');
      })
      .catch((err) => {
        if (!isCurrent()) return;
        if (err?.code === 'SESSION_EXPIRED' || err?.status === 401 || err?.status === 403) {
          sessionRevisionRef.current += 1;
          clearTokens();
          clearRecentSearches();
          candidateSessionRef.current = null;
        }
        setCustomerState(null);
        setAuthStatus('guest');
      });
  }, []);

  useEffect(() => {
    validateSession();
    return () => { sessionRevisionRef.current += 1; };
  }, [validateSession]);

  // A replacement login needs a new /me check, not just new credentials.
  // Token refresh within the same login keeps the validated identity.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== null && event.key !== TOKEN_STORAGE_KEYS.access && event.key !== AUTH_SESSION_STORAGE_KEY) return;
      if (getStoredTokens().accessToken && candidateSessionRef.current === getCurrentSessionId()) return;
      clearRecentSearches();
      validateSession();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [validateSession]);

  const login = useCallback(async (email, password) => {
    const response = await authApi.login(email, password);
    const { accessToken, refreshToken, customer: loggedInCustomer } = response.data;
    sessionRevisionRef.current += 1;
    storeTokens({ accessToken, refreshToken });
    candidateSessionRef.current = getCurrentSessionId();
    setValidatedSession(candidateSessionRef.current);
    setCustomerState(loggedInCustomer);
    setAuthStatus('authenticated');
    return loggedInCustomer;
  }, []);

  const register = useCallback(async (payload) => {
    const response = await authApi.register(payload);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    // Local logout must be immediate and can't be held up by the network —
    // clear credentials first, then revoke the refresh token server-side as
    // a best-effort background call so a slow/hanging request never blocks
    // the UI from signing the user out.
    const { refreshToken } = getStoredTokens();
    sessionRevisionRef.current += 1;
    clearTokens();
    clearRecentSearches();
    candidateSessionRef.current = null;
    setValidatedSession(null);
    setCustomerState(null);
    setAuthStatus('guest');
    if (refreshToken) {
      authApi.logout(refreshToken).catch(() => {
        // Best-effort — the token is already discarded locally either way.
      });
    }
  }, []);

  const refreshCustomer = useCallback(async () => {
    const revision = sessionRevisionRef.current;
    const response = await authApi.getMe({ session: validatedSession });
    if (revision !== sessionRevisionRef.current || validatedSession !== getCurrentSessionId()) return null;
    setCustomerState(response.data);
    return response.data;
  }, [validatedSession]);

  const setCustomer = useCallback((patch) => {
    setCustomerState((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo(
    () => ({ customer, authStatus, validatedSession, login, register, logout, refreshCustomer, setCustomer }),
    [customer, authStatus, validatedSession, login, register, logout, refreshCustomer, setCustomer]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
